// client/src/pages/School_Admin/Finance/FinancialReportsPage.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Tabs, Tab, Box } from '@mui/material';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const FinancialReportsPage = () => {
  const { t, i18n } = useTranslation();
  // Context and cache keys
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  // Year options come from THE SCHOOL'S ACADEMIC YEARS, not from a window
  // counted off today's date.
  //
  // A fixed "this year and the previous five" is dynamic in the sense that it
  // moves, but it is still a guess: it offers years a school has no records for
  // and stops at the current one, so a year that runs into the next calendar
  // year — which every academic year does — cannot be reported on. A school
  // planning next year's finances had no year to select.
  //
  // So the list spans every year the school's academic years touch, plus next
  // year for planning, and never fewer than the last six. Loaded below; until
  // it arrives the fallback keeps the page usable rather than empty.
  const currentYear = new Date().getFullYear();
  const [schoolYears, setSchoolYears] = useState([]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    for (const y of schoolYears) {
      const from = y.startDate ? new Date(y.startDate).getFullYear() : null;
      const to = y.endDate ? new Date(y.endDate).getFullYear() : from;
      if (from) for (let v = from; v <= (to || from); v += 1) years.add(v);
      // Some schools name a year "2025/2026" and leave the dates unset.
      for (const m of String(y.name || '').matchAll(/(20\d{2})/g)) years.add(Number(m[1]));
    }
    years.add(currentYear);
    years.add(currentYear + 1);   // so next year can be budgeted for
    for (let i = 0; i < 6; i += 1) years.add(currentYear - i);
    return [...years].sort((a, b) => b - a);
  }, [schoolYears, currentYear]);

  // The school's years, best-effort: the filter still works without them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchJson('/api/school-academic-years', { method: 'GET' })
          .catch(() => fetchJson('/api/global-academic-years', { method: 'GET' }));
        const list = Array.isArray(res) ? res : (res?.data || res?.academicYears || res?.years || []);
        if (!cancelled && Array.isArray(list)) setSchoolYears(list);
      } catch (e) { /* the fallback window covers this */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // filters will be appended to cache key. Default to a yearly view so the page
  // shows aggregate data on load rather than an often-empty current month.
  const [filters, setFilters] = useState({
    period: 'yearly',
    year: currentYear,
    month: new Date().getMonth() + 1
  });
  const cacheKey = `finance:reports:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}|${filters.period}|${filters.year}|${filters.month}`;

  // Seed reports from cache if any
  const cachedReports = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { return null; } })();
  const [reports, setReports] = useState(cachedReports || { revenue: [], expenses: [], profit: [], outstanding: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const guardRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [tabValue, setTabValue] = useState('overview');

  useEffect(() => {
    // 2s guard if no cache
    const hasCache = reports && ((reports.revenue || []).length > 0 || (reports.expenses || []).length > 0 || (reports.outstanding || []).length > 0 || Object.keys(reports.summary || {}).length > 0);
    if (!hasCache) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    fetchReports();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, [filters]);

  const fetchReports = async () => {
    try {
      // Calculate date range based on filters
      let startDate, endDate;
      if (filters.period === 'monthly') {
        startDate = new Date(filters.year, filters.month - 1, 1).toISOString().split('T')[0];
        endDate = new Date(filters.year, filters.month, 0).toISOString().split('T')[0];
      } else if (filters.period === 'yearly') {
        startDate = new Date(filters.year, 0, 1).toISOString().split('T')[0];
        endDate = new Date(filters.year, 11, 31).toISOString().split('T')[0];
      } else {
        const now = new Date();
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      }

      // Fetch all report types. Use allSettled so one failing endpoint doesn't
      // blank the entire page — render whatever data did come back.
      const results = await Promise.allSettled([
        fetchJson(`/api/finance/reports/comprehensive?startDate=${startDate}&endDate=${endDate}`, { method: 'GET' }),
        fetchJson(`/api/finance/reports/income-statement?startDate=${startDate}&endDate=${endDate}`, { method: 'GET' }),
        fetchJson(`/api/finance/reports/cash-flow?startDate=${startDate}&endDate=${endDate}`, { method: 'GET' }),
        fetchJson(`/api/finance/reports/fee-collection`, { method: 'GET' })
      ]);

      // Normalize data (controllers return { success, report, data: {...}, contextUsed })
      const dataOf = (r) => (r.status === 'fulfilled' && r.value && r.value.data) ? r.value.data : {};
      const comprehensiveData = dataOf(results[0]);
      const incomeData = dataOf(results[1]);
      const feeCollectionData = dataOf(results[3]);

      // Only surface an error if every report failed (true backend/connectivity issue).
      if (results.every(r => r.status === 'rejected')) {
        throw results[0].reason || new Error('All report requests failed');
      }

      const revenueNum = parseFloat(incomeData.revenue || comprehensiveData.revenue || 0);
      const expensesNum = parseFloat(incomeData.totalExpenses || comprehensiveData.expenses || 0);
      const profitNum = parseFloat(incomeData.netIncome || comprehensiveData.profit || 0);

      const singlePeriodLabel = filters.period === 'monthly'
        ? `${filters.year}-${String(filters.month).padStart(2, '0')}`
        : `${filters.year}`;

      // Monthly time series from the backend → real multi-point bar/line charts.
      const monthly = Array.isArray(comprehensiveData.monthly) ? comprehensiveData.monthly : [];
      const overview = monthly.length
        ? monthly.map(m => ({ period: m.period, revenue: parseFloat(m.revenue || 0), expenses: parseFloat(m.expenses || 0) }))
        : [{ period: singlePeriodLabel, revenue: revenueNum, expenses: expensesNum }];

      // Profit trend series (from the same monthly data).
      const profitSeries = monthly.length
        ? monthly.map(m => ({ period: m.period, profit: parseFloat(m.profit || 0) }))
        : [{ period: singlePeriodLabel, profit: profitNum }];

      // Revenue by source (payment method) from the backend; fall back to a single Fees item.
      const revenueBySource = Array.isArray(comprehensiveData.revenueBySource) ? comprehensiveData.revenueBySource : [];
      const revenueBreakdown = revenueBySource.length
        ? revenueBySource.map(r => ({ name: r.source, source: r.source, amount: parseFloat(r.amount || 0) }))
        : [{ name: t('financialReports.labels.fees'), source: t('financialReports.labels.fees'), amount: revenueNum }];

      // Expenses by category list: ensure numeric amounts
      const expensesByCategory = (incomeData.expensesByCategory || []).map(e => ({
        category: e.category,
        amount: parseFloat(e.amount || 0)
      }));

      // Outstanding rows (summary fallback)
      const outstandingRows = [
        {
          studentName: t('financialReports.labels.totalOutstanding'),
          amount: parseFloat((feeCollectionData && feeCollectionData.outstanding) || 0),
          dueDate: new Date().toISOString(),
          daysOverdue: 0
        }
      ];

      const next = {
        overview,
        revenue: revenueBreakdown,
        expenses: expensesByCategory,
        profit: profitSeries,
        outstanding: outstandingRows,
        summary: {
          totalRevenue: revenueNum,
          totalExpenses: expensesNum,
          netProfit: profitNum,
          profitMargin: revenueNum > 0 ? ((profitNum / revenueNum) * 100).toFixed(1) : 0,
          revenueGrowth: 0,
          expenseGrowth: 0
        }
      };
      setReports(next);
      try { localStorage.setItem(cacheKey, JSON.stringify(next)); } catch(_) {}
    } catch (error) {
      setSnackbar({ open: true, message: t('financialReports.errors.fetch'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language || undefined, {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateTotal = (data) => {
    return data.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  if (loading) {
    const hasCache = reports && ((reports.revenue || []).length > 0 || (reports.expenses || []).length > 0 || (reports.outstanding || []).length > 0 || Object.keys(reports.summary || {}).length > 0);
    if (!hasCache) {
      return <AppLoader variant="circular3" size={48} centered messages={[t('financialReports.loading')]} />;
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
        <h1 className="text-2xl font-bold">{t('financialReports.title')}</h1>
        <div className="flex space-x-2">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="period-label">{t('financialReports.filters.period')}</InputLabel>
            <Select
              labelId="period-label"
              label={t('financialReports.filters.period')}
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
            >
              <MenuItem value="daily">{t('financialReports.filters.daily')}</MenuItem>
              <MenuItem value="weekly">{t('financialReports.filters.weekly')}</MenuItem>
              <MenuItem value="monthly">{t('financialReports.filters.monthly')}</MenuItem>
              <MenuItem value="yearly">{t('financialReports.filters.yearly')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="year-label">{t('financialReports.filters.year')}</InputLabel>
            <Select
              labelId="year-label"
              label={t('financialReports.filters.year')}
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value, 10) })}
            >
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {filters.period === 'monthly' && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="month-label">{t('financialReports.filters.month')}</InputLabel>
              <Select
                labelId="month-label"
                label={t('financialReports.filters.month')}
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value, 10) })}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <MenuItem key={month} value={month}>
                    {new Date(filters.year, month - 1, 1).toLocaleDateString(i18n.language || undefined, { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader title={t('financialReports.summary.totalRevenueTitle')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'success.main' }}>
              {formatCurrency(reports.summary.totalRevenue || 0)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('financialReports.summary.fromLastPeriod', { value: reports.summary.revenueGrowth || 0 })}
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title={t('financialReports.summary.totalExpensesTitle')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'error.main' }}>
              {formatCurrency(reports.summary.totalExpenses || 0)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('financialReports.summary.fromLastPeriod', { value: reports.summary.expenseGrowth || 0 })}
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title={t('financialReports.summary.netProfitTitle')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'primary.main' }}>
              {formatCurrency(reports.summary.netProfit || 0)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('financialReports.summary.margin', { value: reports.summary.profitMargin || 0 })}
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title={t('financialReports.summary.outstandingTitle')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'warning.main' }}>
              {formatCurrency(calculateTotal(reports.outstanding) || 0)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('financialReports.summary.pendingCount', { count: reports.outstanding?.length || 0 })}
            </Box>
          </CardContent>
        </Card>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label={t('financialReports.tabs.aria')}>
          <Tab value="overview" label={t('financialReports.tabs.overview')} />
          <Tab value="revenue" label={t('financialReports.tabs.revenue')} />
          <Tab value="expenses" label={t('financialReports.tabs.expenses')} />
          <Tab value="outstanding" label={t('financialReports.tabs.outstanding')} />
        </Tabs>
      </Box>

      {tabValue === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader title={t('financialReports.charts.revenueVsExpenses')} />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reports.overview || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name={t('financialReports.series.revenue')} />
                  <Bar dataKey="expenses" fill="#ef4444" name={t('financialReports.series.expenses')} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader title={t('financialReports.charts.profitTrend')} />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reports.profit}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={2} name={t('financialReports.series.profit')} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {tabValue === 'revenue' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader title={t('financialReports.charts.revenueBySource')} />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reports.revenue}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {reports.revenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader title={t('financialReports.charts.revenueDetails')} />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell component="th">{t('financialReports.tables.source')}</TableCell>
                    <TableCell component="th">{t('financialReports.tables.amount')}</TableCell>
                    <TableCell component="th">{t('financialReports.tables.percentage')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.revenue.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.source}</TableCell>
                      <TableCell>{formatCurrency(item.amount)}</TableCell>
                      <TableCell>
                        {((item.amount / calculateTotal(reports.revenue)) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-bold">{t('financialReports.tables.total')}</TableCell>
                    <TableCell className="font-bold">
                      {formatCurrency(calculateTotal(reports.revenue))}
                    </TableCell>
                    <TableCell className="font-bold">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {tabValue === 'expenses' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader title={t('financialReports.charts.expenseCategories')} />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reports.expenses}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="amount" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader title={t('financialReports.charts.expenseDetails')} />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell component="th">{t('financialReports.tables.category')}</TableCell>
                    <TableCell component="th">{t('financialReports.tables.amount')}</TableCell>
                    <TableCell component="th">{t('financialReports.tables.percentage')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.expenses.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{formatCurrency(item.amount)}</TableCell>
                      <TableCell>
                        {((item.amount / calculateTotal(reports.expenses)) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-bold">{t('financialReports.tables.total')}</TableCell>
                    <TableCell className="font-bold">
                      {formatCurrency(calculateTotal(reports.expenses))}
                    </TableCell>
                    <TableCell className="font-bold">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {tabValue === 'outstanding' && (
        <Card>
          <CardHeader title={t('financialReports.charts.outstandingPayments')} />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell component="th">{t('financialReports.tables.student')}</TableCell>
                  <TableCell component="th">{t('financialReports.tables.amount')}</TableCell>
                  <TableCell component="th">{t('financialReports.tables.dueDate')}</TableCell>
                  <TableCell component="th">{t('financialReports.tables.daysOverdue')}</TableCell>
                  <TableCell component="th">{t('financialReports.tables.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.outstanding.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.studentName}</TableCell>
                    <TableCell>{formatCurrency(item.amount)}</TableCell>
                    <TableCell>{new Date(item.dueDate).toLocaleDateString(i18n.language || undefined)}</TableCell>
                    <TableCell>{item.daysOverdue}</TableCell>
                    <TableCell>
                      <Chip size="small" label={item.daysOverdue > 30 ? t('financialReports.status.overdue') : t('financialReports.status.pending')} color={item.daysOverdue > 30 ? 'error' : 'warning'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FinancialReportsPage;
